// src/pages/PrivacyPolicy.jsx
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";

function slugify(str = "") {
  return String(str)
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

export default function PrivacyPolicy() {
  const { t } = useTranslation();
  const sections = t("legal.privacy.sections", { returnObjects: true }) || [];
  const list = Array.isArray(sections) ? sections : [];
  const operator = t("legal.operator", { returnObjects: true }) || {};
  const lastUpdated = t("legal.last_updated");

  const ui = t("legal.ui", { returnObjects: true }) || {};
  const fields = ui.fields || {};

  const toc = useMemo(
    () => list.map((s) => ({ text: s?.h ?? "", id: slugify(s?.h ?? "") })),
    [list]
  );

  return (
    <>

      {/* Color header band */}
      <section className="border-b border-slate-200 bg-gradient-to-r from-indigo-50 via-violet-50 to-fuchsia-50">
        <div className="mx-auto max-w-7xl px-4 py-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 text-xs font-medium text-indigo-700 ring-1 ring-indigo-200">
            <span>{ui.badge}</span>
            <span className="text-slate-400">•</span>
            <span>{t("legal.privacy.title")}</span>
          </div>
          <h1 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
            {t("legal.privacy.title")}
          </h1>
          <p className="mt-2 text-sm text-slate-600">{lastUpdated}</p>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-4 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* TOC */}
        <aside className="hidden lg:block lg:col-span-3">
          <div className="sticky top-24 rounded-2xl border border-indigo-100 bg-white p-4 shadow-[0_1px_0_0_rgba(99,102,241,.15)]">
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700 mb-3">
              {ui.contents}
            </p>
            <nav className="space-y-1.5">
              {toc.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className="block rounded-lg px-2 py-1.5 text-sm text-slate-700 hover:text-indigo-700 hover:bg-indigo-50"
                >
                  {item.text}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main content */}
        <div className="lg:col-span-9 space-y-6">
          {/* Operator card */}
          <div className="relative rounded-2xl border border-slate-200 bg-white p-6">
            <div className="absolute inset-x-0 -top-[1px] h-[3px] bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500 rounded-t-2xl" />
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">
                  {ui.operator_label}
                </p>
                <h2 className="text-xl font-semibold mt-1 text-slate-900">
                  {operator?.name} <span className="text-slate-400">/</span>{" "}
                  <span className="text-slate-700">{operator?.brand}</span>
                </h2>
                <p className="mt-1 text-sm text-slate-600">{operator?.site}</p>
              </div>
              <dl className="text-sm text-slate-700 space-y-1.5">
                <div><dt className="inline text-slate-500">{fields.address}: </dt><dd className="inline">{operator?.address || "București, Sector 1, B-dul Bucureștii Noi, nr. 136, Parter, ap. 5"}</dd></div>
                <div><dt className="inline text-slate-500">{fields.cui}: </dt><dd className="inline">{operator?.cui || "52624295"}</dd></div>
                <div><dt className="inline text-slate-500">{fields.onrc}: </dt><dd className="inline">{operator?.onrc || "F2025037812000"}</dd></div>
                <div>
                  <dt className="inline text-slate-500">{fields.email}: </dt>
                  <dd className="inline">
                    <a className="text-indigo-700 hover:underline" href={`mailto:${operator?.email || ""}`}>
                      {operator?.email || "andreitechro@gmail.com"}
                    </a>
                  </dd>
                </div>
                <div><dt className="inline text-slate-500">{fields.phone}: </dt><dd className="inline">{operator?.phone || "[+40 752 026 975]"}</dd></div>
              </dl>
            </div>
          </div>

          {/* Sections */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            {list.map((s, idx) => {
              const id = slugify(s?.h ?? `section-${idx}`);
              return (
                <section key={id} id={id} className={idx ? "mt-8" : ""}>
                  <h3 className="text-xl font-semibold mb-2 text-slate-900">{s?.h}</h3>
                  <div
                    className="prose prose-slate max-w-none prose-a:text-indigo-700 hover:prose-a:underline"
                    dangerouslySetInnerHTML={{ __html: s?.p || "" }}
                  />
                </section>
              );
            })}
          </div>
        </div>
      </main>
    </>
  );
}
